import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, Plus } from 'lucide-react';
import ProgramsTab, { EnrollSheet } from '../components/lawncare/Programs';
import AddonsTab from '../components/lawncare/Addons';
import ThisWeekTab from '../components/lawncare/ThisWeek';

/**
 * Lawn care — programs and add-ons as one system (owner, Nov 2 2026: "revamp
 * mowing add-ons, combine with treatments, and revamp the whole system").
 *
 * Three tabs on one page. Nothing is stored, priced, scheduled or billed any
 * differently than before: Programs is the old Treatments page tidied to one
 * card per customer, Add-ons is the old Mowing add-ons page with what rides
 * each schedule and what is coming up, This week is both laid out by day.
 * /treatments and /mow-addons still work — they open this page on their tab.
 */
export type LawnCareTab = 'programs' | 'addons' | 'week';
const TABS: [LawnCareTab, string][] = [['programs', 'Programs'], ['addons', 'Add-ons'], ['week', 'This week']];
const EXPLAIN: Record<LawnCareTab, string> = {
  programs: 'A program is a yearly plan of applications (feed, pre-emergent, weed control…) priced off the Plan Cost Sheet by the lawn’s size and billed once a month. Each application is a round with a real visit on the calendar, riding the yard’s mow where there is one.',
  addons: 'An add-on is an extra that rides a mow — bed spray, a weed treatment, a bush trim. It can go on one visit, or on a schedule (every mow, every other, once a month…), tells the crew what to do, adds its minutes, and bills its own line at month end.',
  week: 'Everything due in the next 7 days, by day: each program round, each old-job visit, each add-on, and the mow it rides. Read only — open the job to change it.',
};

export default function LawnCare({ tab: fixed }: { tab?: LawnCareTab } = {}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('tab') as LawnCareTab | null;
  const tab: LawnCareTab = fixed || (fromUrl && TABS.some(([k]) => k === fromUrl) ? fromUrl : 'programs');
  const [enrolling, setEnrolling] = useState(false);
  const go = (k: LawnCareTab) => {
    // /treatments and /mow-addons open on their own tab; from there a tap goes to the one page.
    if (fixed) navigate(`/lawn-care?tab=${k}`);
    else setParams(k === 'programs' ? {} : { tab: k }, { replace: true });
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Lawn care</h1>
          <p className="text-sm text-gray-500">Programs and add-ons — everything extra done on a lawn on a rhythm.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* The cost sheet runs the plans: prices, calendar, crew amounts, buy list, flyer. */}
          <button onClick={() => navigate('/treatments/sheet')} className="btn-secondary text-sm inline-flex items-center gap-1"><Leaf className="h-4 w-4" /> Plan Cost Sheet</button>
          <button onClick={() => setEnrolling(true)} className="btn-primary text-sm inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Start a program</button>
        </div>
      </div>
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => go(k)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{label}</button>
        ))}
      </div>
      <p className="text-xs text-gray-600 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">{EXPLAIN[tab]}</p>
      {tab === 'programs' ? <ProgramsTab /> : tab === 'addons' ? <AddonsTab /> : <ThisWeekTab />}
      {enrolling && <EnrollSheet onClose={() => setEnrolling(false)} />}
    </div>
  );
}
